import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "@/hooks/use-business";

export interface Product {
  id: string;
  business_id: string;
  name: string;
  description: string | null;
  price_amount: number;
  currency: string;
  quantity_in_stock: number | null;
  image_url: string | null;
  media_items: { url: string; type: "image" | "video" }[] | null;
  active: boolean;
  position: number;
  created_at: string;
  updated_at: string;
}

export function useProducts() {
  const { business } = useBusiness();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async () => {
    if (!business?.id) return;
    setLoading(true);
    try {
      const { data, error } = await (supabase as any)
        .from("business_products")
        .select("*")
        .eq("business_id", business.id)
        .order("position", { ascending: true })
        .order("created_at", { ascending: false });
      if (error) throw error;
      setProducts(data || []);
    } catch (e) {
      console.error("[useProducts] fetch failed", e);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [business?.id]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const createProduct = async (payload: Partial<Product>): Promise<Product> => {
    if (!business?.id) throw new Error("No business");
    const { data, error } = await (supabase as any)
      .from("business_products")
      .insert({ ...payload, business_id: business.id, created_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .select()
      .single();
    if (error) throw error;
    await fetchProducts();
    return data as Product;
  };

  const updateProduct = async (id: string, payload: Partial<Product>): Promise<void> => {
    const { error } = await (supabase as any)
      .from("business_products")
      .update({ ...payload, updated_at: new Date().toISOString() })
      .eq("id", id);
    if (error) throw error;
    await fetchProducts();
  };

  const archiveProduct = async (id: string): Promise<void> => {
    await updateProduct(id, { active: false });
  };

  return { products, loading, refetch: fetchProducts, createProduct, updateProduct, archiveProduct };
}
